import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Filter, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { SEO } from '../components/common/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { DealCard } from '../components/common/DealCard';
import { AdBanner, isAdsEnabled } from '../components/common/AdBanner';
import { categories, offerTypes, cities, getAreasForCity } from '../data/constants';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const DealsPage = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const category = searchParams.get('category');
  const area = searchParams.get('area');
  const maxPrice = searchParams.get('max_price');
  const offerTypeFilter = searchParams.get('offer_type');
  const cityParam = searchParams.get('city');
  const pageParam = parseInt(searchParams.get('page') || '1', 10);

  const [selectedCity, setSelectedCity] = useState(cityParam || localStorage.getItem('selectedCity') || '');
  const [currentPage, setCurrentPage] = useState(pageParam);
  const [filters, setFilters] = useState({
    category: category || '',
    area: area || '',
    maxPrice: maxPrice || '',
    offerType: offerTypeFilter || ''
  });

  const handleCityChange = (city) => {
    setSelectedCity(city);
    setFilters({ ...filters, area: '' });
    setCurrentPage(1); // Reset to page 1 on filter change
    if (city) {
      localStorage.setItem('selectedCity', city);
    } else {
      localStorage.removeItem('selectedCity');
    }
  };

  useEffect(() => { fetchDeals(currentPage); }, [category, area, maxPrice, offerTypeFilter, selectedCity, currentPage]);

  const fetchDeals = async (page = 1, retryCount = 0) => {
    setLoading(true);
    try {
      let url = `${API}/api/deals?page=${page}&limit=25&`;
      // Only add city filter if city is selected
      if (selectedCity) url += `city=${encodeURIComponent(selectedCity)}&`;
      if (category) url += `category=${category}&`;
      if (area) url += `area=${encodeURIComponent(area)}&`;
      if (maxPrice) url += `max_price=${maxPrice}&`;
      if (offerTypeFilter) url += `offer_type=${encodeURIComponent(offerTypeFilter)}&`;
      url += `_t=${Date.now()}`;
      console.log('DealsPage fetching:', url);
      const res = await axios.get(url, { timeout: 10000 });
      
      // Handle paginated response
      if (res.data.deals && res.data.pagination) {
        setDeals(res.data.deals);
        setPagination(res.data.pagination);
        console.log('DealsPage loaded:', res.data.deals.length, 'deals, total:', res.data.pagination.total);
      } else {
        // Fallback for old API format
        setDeals(res.data);
        setPagination({
          page: 1,
          limit: 25,
          total: res.data.length,
          total_pages: 1,
          has_next: false,
          has_prev: false
        });
      }
    } catch (e) {
      console.error('DealsPage fetch error:', e);
      if (retryCount < 3) {
        setTimeout(() => fetchDeals(page, retryCount + 1), 1000);
        return;
      }
      toast.error('Failed to load deals. Please refresh.');
    }
    setLoading(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.total_pages) return;
    setCurrentPage(newPage);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', newPage.toString());
    setSearchParams(newParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const applyFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    newParams.set('page', '1'); // Reset to page 1 on filter change
    setSearchParams(newParams);
    setFilters({ ...filters, [key]: value });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchParams({});
    setFilters({ category: '', area: '', maxPrice: '', offerType: '' });
  };

  const getTitle = () => {
    const cityText = selectedCity || 'All Cities';
    if (offerTypeFilter) return `${offerTypeFilter} in ${cityText}`;
    if (category) { const cat = categories.find(c => c.id === category); return cat ? `${cat.name} Deals in ${cityText}` : 'Deals'; }
    if (area) return `Deals in ${area}${selectedCity ? `, ${selectedCity}` : ''}`;
    if (maxPrice) return `Deals Under ₹${maxPrice}${selectedCity ? ` in ${selectedCity}` : ''}`;
    return selectedCity ? `All Deals in ${selectedCity}` : 'All Deals';
  };

  const hasFilters = category || filters.area || maxPrice || offerTypeFilter;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="deals-page">
      <SEO title={`Browse Deals${selectedCity ? ` in ${selectedCity}` : ''} - JustOffs | Local Offers`} description={`Explore the best local deals, discounts, and offers near you${selectedCity ? ` in ${selectedCity}` : ''}. Filter by category, area, and price range.`} />
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-4">
          <Link to="/" className="p-2 hover:bg-slate-200 rounded-lg transition" data-testid="back-link"><ArrowLeft className="w-5 h-5" /></Link>
          <h1 className="text-xl font-bold text-slate-800">{getTitle()}</h1>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 mb-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="font-medium text-slate-700 text-sm">Filters</span>
            {hasFilters && (
              <button onClick={clearFilters} className="text-orange-500 text-xs ml-auto hover:underline" data-testid="clear-filters">Clear All</button>
            )}
          </div>
          
          {/* City Selector */}
          <div className="mb-4">
            <p className="text-xs text-slate-500 mb-2">City</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => handleCityChange('')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${!selectedCity ? 'bg-orange-500 text-white border-orange-500' : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300'}`} data-testid="city-filter-all">
                All Cities
              </button>
              {cities.filter(city => city !== 'Other').map(city => (
                <button key={city} onClick={() => handleCityChange(city)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${selectedCity === city ? 'bg-orange-500 text-white border-orange-500' : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300'}`} data-testid={`city-filter-${city}`}>
                  {city}
                </button>
              ))}
            </div>
          </div>
          
          {/* Offer Type Filters */}
          <div className="mb-4">
            <p className="text-xs text-slate-500 mb-2">Offer Type</p>
            <div className="flex flex-wrap gap-2">
              {offerTypes.map(type => (
                <button key={type.id} onClick={() => applyFilter('offer_type', offerTypeFilter === type.id ? '' : type.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${offerTypeFilter === type.id ? type.color + ' border-2' : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300'}`} data-testid={`offer-filter-${type.id}`}>
                  {type.emoji} {type.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select value={filters.category} onChange={(e) => applyFilter('category', e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="category-filter">
              <option value="">All Categories</option>
              {categories.map(cat => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
            </select>
            <select value={filters.area} onChange={(e) => { setFilters({...filters, area: e.target.value}); applyFilter('area', e.target.value); }}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="area-filter">
              <option value="">All Areas</option>
              {getAreasForCity(selectedCity).map(a => (<option key={a.id} value={a.name}>{a.name}</option>))}
            </select>
            <select value={filters.maxPrice} onChange={(e) => applyFilter('max_price', e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="price-filter">
              <option value="">Any Price</option>
              <option value="100">Under ₹100</option>
              <option value="200">Under ₹200</option>
              <option value="300">Under ₹300</option>
              <option value="500">Under ₹500</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100 animate-pulse">
                <div className="h-40 bg-slate-200"></div>
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                  <div className="h-3 bg-slate-200 rounded w-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : deals.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="deals-grid">
              {deals.map((deal, index) => (
                <React.Fragment key={deal.id}>
                  <DealCard deal={deal} />
                  {/* Ad slot after every 8 deals - only if AdSense is configured */}
                  {isAdsEnabled() && (index + 1) % 8 === 0 && index !== deals.length - 1 && (
                    <div className="col-span-2 md:col-span-3 lg:col-span-4 my-4">
                      <AdBanner slot="horizontal-deals" format="horizontal" className="bg-slate-50 rounded-lg p-2 min-h-[90px]" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.has_prev}
                  className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium text-sm transition ${
                    pagination.has_prev 
                      ? 'bg-white border border-slate-200 text-slate-700 hover:border-orange-400 hover:text-orange-600' 
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  }`}
                  data-testid="prev-page"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.total_pages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= pagination.total_pages - 2) {
                      pageNum = pagination.total_pages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg font-medium text-sm transition ${
                          currentPage === pageNum
                            ? 'bg-orange-500 text-white'
                            : 'bg-white border border-slate-200 text-slate-700 hover:border-orange-400'
                        }`}
                        data-testid={`page-${pageNum}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.has_next}
                  className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium text-sm transition ${
                    pagination.has_next 
                      ? 'bg-white border border-slate-200 text-slate-700 hover:border-orange-400 hover:text-orange-600' 
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  }`}
                  data-testid="next-page"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
            
            {/* Pagination Info */}
            <p className="text-center text-slate-500 text-sm mt-4">
              Showing {deals.length} of {pagination.total} deals (Page {pagination.page} of {pagination.total_pages})
            </p>
          </>
        ) : (
          <div className="text-center py-12 bg-white rounded-2xl">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No deals found</h3>
            <p className="text-slate-500 text-sm">Try different filters</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};
